import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    Heart,
    Users,
    Award,
    MapPin,
    DollarSign,
    Send,
    CheckCircle,
    Stethoscope,
    GraduationCap,
    Shield,
    ChevronDown,
    UploadCloud,
    ChevronUp,
} from 'lucide-react';

/* ─── Shared helpers ─────────────────────────────────────────────────────── */
const inputCls =
    'w-full px-5 py-4 border-2 border-gray-200 rounded-xl bg-white ' +
    'focus:outline-none focus:border-granby-blue-light transition-colors ' +
    'text-gray-800 placeholder:text-gray-400';

const FieldLabel = ({ children, required }) => (
    <label className="block text-sm font-semibold text-gray-700 mb-2">
        {children}
        {required && <span className="text-granby-violet ml-1">*</span>}
    </label>
);

const SelectField = ({ label, name, value, onChange, children, required }) => (
    <div>
        <FieldLabel required={required}>{label}</FieldLabel>
        <div className="relative">
            <ChevronDown
                size={17}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10"
                aria-hidden="true"
            />
            <select
                name={name} value={value} onChange={onChange} required={required}
                className={`${inputCls} appearance-none pr-11 cursor-pointer`}
            >
                {children}
            </select>
        </div>
    </div>
);

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────── */
const Careers = () => {
    const [selectedJob, setSelectedJob] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        availability: '',
        resume: null,
        coverLetterFile: null,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setSubmitError('');
        if (!formData.resume) {
            setSubmitError('Veuillez joindre votre CV.');
            setIsLoading(false);
            return;
        }
        try {
            const body = new FormData();
            Object.entries(formData).forEach(([key, val]) => {
                if (val !== null) body.append(key, val);
            });
            const res = await fetch('/api/careers', {
                method: 'POST',
                body,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Erreur serveur');
            setIsSubmitted(true);
        } catch {
            setSubmitError('Une erreur est survenue. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] || null;
        setFormData((p) => ({ ...p, [e.target.name]: file }));
    };

    const benefits = [
        {
            title: 'Salaire compétitif',
            description: "Rémunération attractive selon l'expérience",
            icon: DollarSign,
            bg: 'bg-granby-bg-blue',
            fg: 'text-granby-blue',
        },
        {
            title: 'Formation continue',
            description: 'Perfectionnement en troubles neurocognitifs',
            icon: GraduationCap,
            bg: 'bg-granby-bg-violet',
            fg: 'text-granby-violet',
        },
        {
            title: 'Gestion Humaine',
            description: 'Environnement de travail positif et collaboratif',
            icon: Users,
            bg: 'bg-granby-bg-blue',
            fg: 'text-granby-blue',
        },
        {
            title: 'Avantages sociaux compétitifs',
            description: 'Cadeaux, repas gratuits et pauses rémunérées',
            icon: Shield,
            bg: 'bg-granby-bg-violet',
            fg: 'text-granby-violet',
        },
    ];

    const positions = [
        {
            id: 1,
            title: "Préposé(e) aux bénéficiaires",
            type: "Temps plein / Temps partiel",
            department: "Soins directs",
            description:
                "Rejoignez notre équipe dédiée au bien-être quotidien des résidents dans un environnement respectueux et bienveillant.",
            requirements: [
                "Certificat de préposé aux bénéficiaires",
                "Formation RCR à jour",
                "Patience et empathie",
                "Capacité de travailler en équipe",
                "Flexibilité d'horaire",
            ],
            responsibilities: [
                "Aide aux soins d'hygiène et d'habillage",
                "Assistance aux repas",
                "Accompagnement dans les déplacements",
                "Animation d'activités simples",
                "Surveillance de sécurité",
            ],
        },
        {
            id: 2,
            title: "Aide-Cuisine / Plongeur",
            type: "Temps plein / Temps partiel",
            department: "Service alimentaire",
            description:
                "Soutenez l'équipe de cuisine en veillant à la propreté et à l'organisation, tout en contribuant à la préparation des repas destinés aux résidents.",
            requirements: [
                "Expérience en cuisine ou plonge (atout, mais non obligatoire)",
                "Bonne condition physique et rapidité d'exécution",
                "Respect strict des normes d'hygiène et de salubrité",
                "Capacité à travailler en équipe et sous pression",
                "Ponctualité et sens des responsabilités",
            ],
            responsibilities: [
                "Nettoyage et désinfection de la vaisselle, des ustensiles et du matériel",
                "Aide à la préparation de repas simples",
                "Maintien de la propreté des espaces de travail",
                "Gestion et rangement de la vaisselle et du matériel",
                "Soutien aux cuisiniers et respect des consignes de sécurité alimentaire",
            ],
        },
    ];

    const companyValues = [
        {
            title: 'Performance',
            description: "Nous cherchons toujours à dépasser les attentes en offrant un service de qualité et en visant l'excellence dans chaque tâche.",
            icon: Award,
        },
        {
            title: 'Assiduité',
            description: "La constance et la rigueur sont au cœur de notre engagement, car chaque détail compte dans le bien-être des résidents.",
            icon: CheckCircle,
        },
        {
            title: "Esprit d'Équipe",
            description: "Nous croyons que la collaboration et le soutien mutuel sont essentiels pour bâtir un environnement de travail harmonieux.",
            icon: Users,
        },
        {
            title: 'Approche Adaptée',
            description: "Chaque résident est unique : nous adaptons nos soins et notre accompagnement à ses besoins spécifiques, avec respect et bienveillance.",
            icon: Heart,
        },
    ];

    return (
        <div className="font-sans text-gray-900">

            {/* ── HERO ──────────────────────────────────────────────────── */}
            <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage:
                            'linear-gradient(135deg, rgba(166, 204, 230, 0.92), rgba(186, 174, 217, 0.92))',
                    }}
                    aria-hidden="true"
                />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <h1 className="text-5xl lg:text-6xl font-athelas font-bold text-white mb-6">
                            Carrières
                        </h1>
                        <p className="text-xl lg:text-2xl text-white/90 leading-relaxed">
                            Ensemble, créons un environnement de soins exceptionnel.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ── AVANTAGES ─────────────────────────────────────────────── */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl lg:text-5xl font-athelas font-bold mb-6">
                            Pourquoi travailler avec nous&nbsp;?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Découvrez un environnement de travail enrichissant où votre passion peut s'épanouir.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 lg:gap-12">
                        {benefits.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <motion.div
                                    key={b.title}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className="text-center group"
                                >
                                    <div className={`w-16 h-16 ${b.bg} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                        <Icon size={32} className={b.fg} />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{b.title}</h3>
                                    <p className="text-gray-600">{b.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── VALEURS ───────────────────────────────────────────────── */}
            <section className="py-20 bg-granby-bg-blue">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl lg:text-5xl font-athelas font-bold mb-6">Nos valeurs</h2>
                        <p className="text-lg text-gray-600">Les principes qui guident notre mission au quotidien.</p>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 lg:gap-12">
                        {companyValues.map((v, i) => {
                            const Icon = v.icon;
                            return (
                                <motion.div
                                    key={v.title}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className="bg-white rounded-2xl p-10 shadow-granby hover:shadow-granby-lg transition-all duration-300 text-center flex flex-col"
                                >
                                    <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 bg-granby-bg-blue">
                                        <Icon size={32} className="text-granby-blue-light" />
                                    </div>
                                    <h3 className="text-2xl font-athelas font-bold text-gray-900 mb-3">{v.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{v.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── POSTES ────────────────────────────────────────────────── */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl lg:text-5xl font-athelas font-bold mb-6">Postes disponibles</h2>
                        <p className="text-xl text-gray-600">Explorez nos opportunités et trouvez le poste qui vous correspond.</p>
                    </motion.div>

                    <div className="space-y-6">
                        {positions.map((p, i) => {
                            const isOpen = selectedJob === p.id;
                            return (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className="bg-white rounded-2xl shadow-granby hover:shadow-granby-lg transition-all duration-300 overflow-hidden border border-gray-100"
                                >
                                    <div className="p-8">
                                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                                            <div className="flex-1">
                                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                                    <h3 className="text-2xl font-athelas font-bold text-gray-900">{p.title}</h3>
                                                    <span className="bg-granby-bg-blue text-granby-blue-dark px-3 py-1 rounded-full text-sm font-medium border border-granby-blue/20">
                                                        {p.type}
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-500 text-sm">
                                                    <div className="flex items-center gap-1.5">
                                                        <Stethoscope size={15} />
                                                        <span>{p.department}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <MapPin size={15} />
                                                        <span>Granby, QC</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => setSelectedJob(isOpen ? null : p.id)}
                                                className="inline-flex items-center gap-2 bg-granby-blue text-white px-6 py-3 rounded-xl
                                                    hover:shadow-granby transition-all duration-300 font-medium
                                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-granby-blue-light"
                                            >
                                                {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                                {isOpen ? 'Masquer' : 'Voir les détails'}
                                            </button>
                                        </div>

                                        <p className="text-gray-600 leading-relaxed">{p.description}</p>

                                        {isOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                className="border-t border-gray-100 pt-6 mt-6"
                                            >
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                    <div>
                                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 font-athelas">Exigences</h4>
                                                        <ul className="space-y-2">
                                                            {p.requirements.map((req, idx) => (
                                                                <li key={idx} className="flex items-start gap-2">
                                                                    <CheckCircle className="text-granby-blue mt-0.5 shrink-0" size={16} />
                                                                    <span className="text-gray-700">{req}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <div>
                                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 font-athelas">Responsabilités</h4>
                                                        <ul className="space-y-2">
                                                            {p.responsibilities.map((resp, idx) => (
                                                                <li key={idx} className="flex items-start gap-2">
                                                                    <CheckCircle className="text-granby-violet mt-0.5 shrink-0" size={16} />
                                                                    <span className="text-gray-700">{resp}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── FORMULAIRE ────────────────────────────────────────────── */}
            <section className="py-20 bg-gradient-to-b from-granby-bg-violet/30 to-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="bg-white rounded-2xl shadow-granby-lg overflow-hidden border border-gray-100"
                    >
                        {/* Card header */}
                        <div className="bg-granby-violet px-8 py-8 text-white">
                            <h2 className="text-3xl font-athelas font-bold mb-2">Postulez maintenant</h2>
                            <p className="text-white/85">
                                Envoyez-nous votre candidature et rejoignez notre équipe dévouée.
                            </p>
                        </div>

                        {isSubmitted ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="p-12 text-center"
                            >
                                <CheckCircle className="text-granby-blue mx-auto mb-4" size={56} />
                                <h3 className="text-2xl font-athelas font-bold text-gray-900 mb-2">Candidature envoyée !</h3>
                                <p className="text-gray-500">Nous examinerons votre dossier et vous contacterons rapidement.</p>
                            </motion.div>
                        ) : (
                            <form onSubmit={handleSubmit} className="p-8 space-y-6">

                                {/* Nom / Prénom */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <FieldLabel required>Prénom</FieldLabel>
                                        <input type="text" name="firstName" required
                                            value={formData.firstName} onChange={handleChange}
                                            placeholder="Votre prénom"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <FieldLabel required>Nom</FieldLabel>
                                        <input type="text" name="lastName" required
                                            value={formData.lastName} onChange={handleChange}
                                            placeholder="Votre nom de famille"
                                            className={inputCls}
                                        />
                                    </div>
                                </div>

                                {/* Email / Téléphone */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <FieldLabel required>Email</FieldLabel>
                                        <input type="email" name="email" required
                                            value={formData.email} onChange={handleChange}
                                            placeholder="votre@email.com"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <FieldLabel required>Téléphone</FieldLabel>
                                        <input type="tel" name="phone" required
                                            value={formData.phone} onChange={handleChange}
                                            placeholder="(450) 000-0000"
                                            className={inputCls}
                                        />
                                    </div>
                                </div>

                                {/* Poste */}
                                <SelectField
                                    label="Poste visé" name="position"
                                    value={formData.position} onChange={handleChange} required
                                >
                                    <option value="">Sélectionnez un poste</option>
                                    {positions.map((pos) => (
                                        <option key={pos.id} value={pos.title}>{pos.title}</option>
                                    ))}
                                </SelectField>

                                {/* Expérience / Disponibilité */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <SelectField
                                        label="Années d'expérience" name="experience"
                                        value={formData.experience} onChange={handleChange}
                                    >
                                        <option value="">Sélectionnez</option>
                                        <option value="0-1">0 – 1 an</option>
                                        <option value="2-5">2 – 5 ans</option>
                                        <option value="6-10">6 – 10 ans</option>
                                        <option value="10+">Plus de 10 ans</option>
                                    </SelectField>

                                    <SelectField
                                        label="Disponibilité" name="availability"
                                        value={formData.availability} onChange={handleChange}
                                    >
                                        <option value="">Sélectionnez</option>
                                        <option value="immediat">Immédiatement</option>
                                        <option value="2semaines">Dans 2 semaines</option>
                                        <option value="1mois">Dans 1 mois</option>
                                        <option value="flexible">Flexible</option>
                                    </SelectField>
                                </div>

                                {/* Upload CV */}
                                <div>
                                    <FieldLabel required>CV (PDF, DOC, DOCX)</FieldLabel>
                                    <input
                                        type="file" id="resume" name="resume"
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="resume"
                                        className={`flex flex-col items-center justify-center gap-3 w-full py-8 px-6
                                            rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
                                            ${formData.resume
                                                ? 'border-granby-blue-light bg-granby-bg-blue'
                                                : 'border-gray-200 hover:border-granby-blue/50 hover:bg-gray-50'
                                            }`}
                                    >
                                        <UploadCloud
                                            size={32}
                                            className={formData.resume ? 'text-granby-blue-dark' : 'text-gray-400'}
                                        />
                                        {formData.resume ? (
                                            <div className="text-center">
                                                <p className="font-semibold text-granby-blue-dark">{formData.resume.name}</p>
                                                <p className="text-xs text-gray-500 mt-1">Cliquez pour changer de fichier</p>
                                            </div>
                                        ) : (
                                            <div className="text-center">
                                                <p className="font-medium text-gray-700">Glissez votre CV ici</p>
                                                <p className="text-sm text-gray-400 mt-1">ou cliquez pour parcourir</p>
                                            </div>
                                        )}
                                    </label>
                                </div>

                                {/* Lettre de motivation */}
                                <div>
                                    <FieldLabel>Lettre de motivation </FieldLabel>
                                    <input
                                        type="file" id="coverLetterFile" name="coverLetterFile"
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="coverLetterFile"
                                        className={`flex flex-col items-center justify-center gap-3 w-full py-8 px-6
                                            rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
                                            ${formData.coverLetterFile
                                                ? 'border-granby-blue-light bg-granby-bg-blue'
                                                : 'border-gray-200 hover:border-granby-blue/50 hover:bg-gray-50'
                                            }`}
                                    >
                                        <UploadCloud
                                            size={32}
                                            className={formData.coverLetterFile ? 'text-granby-blue-dark' : 'text-gray-400'}
                                        />
                                        {formData.coverLetterFile ? (
                                            <div className="text-center">
                                                <p className="font-semibold text-granby-blue-dark">{formData.coverLetterFile.name}</p>
                                                <p className="text-xs text-gray-500 mt-1">Cliquez pour changer de fichier</p>
                                            </div>
                                        ) : (
                                            <div className="text-center">
                                                <p className="font-medium text-gray-700">Glissez votre lettre ici</p>
                                                <p className="text-sm text-gray-400 mt-1">ou cliquez pour parcourir</p>
                                            </div>
                                        )}
                                    </label>
                                </div>

                                {/* Submit */}
                                <motion.button
                                    type="submit"
                                    disabled={isLoading}
                                    whileHover={{ scale: isLoading ? 1 : 1.01 }}
                                    whileTap={{ scale: isLoading ? 1 : 0.99 }}
                                    className="w-full py-5 px-6 rounded-xl bg-granby-violet text-white font-semibold text-lg
                                        flex items-center justify-center gap-3 shadow-granby hover:shadow-granby-lg
                                        transition-shadow duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-granby-violet
                                        disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    <Send size={20} aria-hidden="true" />
                                    {isLoading ? 'Envoi en cours…' : 'Envoyer ma candidature'}
                                </motion.button>
                                {submitError && (
                                    <p className="text-red-500 text-sm text-center mt-2">{submitError}</p>
                                )}
                                <p className="text-sm text-gray-400 text-center">
                                    Nous examinerons votre candidature avec attention et vous contacterons sous 48 heures.
                                </p>
                            </form>
                        )}
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Careers;
